import { useEffect, useRef, useState } from "react";
import {
  CalendarDays,
  CircleCheck,
  FileText,
  RefreshCw,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import {
  stateLabels,
  type RequestDetail,
  type SessionInfo,
} from "../../../../shared/labels/contracts";
import { planExplanation } from "../../../../shared/labels/plan-visibility";
import { ConfirmDialog } from "../../../shared/components/ConfirmDialog";
import { SystemState } from "../../../shared/components/SystemState";
import type { useLabelWorkspace } from "../useLabelWorkspace";
import type { useSalesPlanWorkspace } from "../useSalesPlanWorkspace";
import { requirementsDiffer } from "../ui-types";
import { LabelRecordHeader } from "./LabelRecordHeader";
import { CompletedSalesList } from "./CompletedSalesList";
import { FileUpload } from "./FilePanels";
import { SalesForm } from "./SalesForm";
import { SalesPlanSheet } from "./SalesPlanSheet";
import "../sales-dashboard.css";

type Work = ReturnType<typeof useLabelWorkspace>;
type Plan = ReturnType<typeof useSalesPlanWorkspace>;

export function SalesDashboard({
  plan,
  work,
  session,
}: {
  plan: Plan;
  work: Work;
  session: SessionInfo;
}) {
  const [section, setSection] = useState<"mine" | "plan" | "completed">("mine");
  const [expandedExplanation, setExpandedExplanation] = useState<string | null>(
    null,
  );
  const detail = work.detail;
  const requirementsDirty = Boolean(
    detail && requirementsDiffer(work.draft, detail.draft),
  );
  const items = work.list?.items ?? [];
  const needsAction = items.filter(
    (item) =>
      item.state === "sales_pending" || item.state === "returned_to_sales",
  );
  const completedItems = items.filter(
    (item) => item.state === "print_completed",
  );
  const navigationGuard = work.mutationBusy
    ? "Kaydetme işlemi bitmeden bölüm değiştirilemez."
    : work.pendingUpload
      ? "Seçilen dosyayı yükleyin veya seçimi temizleyin."
      : work.dirty
        ? "Taslağı kaydedin; ardından bölüm değiştirebilirsiniz."
        : null;
  const navigationBlocked = Boolean(navigationGuard);
  const openSection = (next: "mine" | "plan" | "completed") => {
    if (navigationBlocked) return;
    if (work.selectedId) work.closeDetail();
    setSection(next);
    if (next === "mine")
      work.setQuery({
        ...work.query,
        state: undefined,
        department: "sales",
        page: 1,
      });
    if (next === "completed")
      work.setQuery({
        ...work.query,
        state: "print_completed",
        department: undefined,
        page: 1,
      });
  };

  useEffect(() => {
    if (detail?.state === "print_completed") setSection("completed");
  }, [detail?.state]);

  return (
    <div className="anl-sales-home">
      <div
        className={
          "anl-sales-home-layout" + (work.selectedId ? " is-detail" : "")
        }
      >
        <nav
          className="anl-sales-sections"
          aria-label="Satış bölümleri"
          aria-describedby={
            navigationGuard ? "sales-navigation-guard" : undefined
          }
        >
          <button
            type="button"
            disabled={navigationBlocked}
            aria-current={section === "mine" ? "page" : undefined}
            onClick={() => openSection("mine")}
          >
            <FileText size={18} aria-hidden="true" />
            Bana atanan
          </button>
          {session.demo || session.actor?.requestIds === null ? (
            <button
              type="button"
              disabled={navigationBlocked}
              aria-current={section === "plan" ? "page" : undefined}
              onClick={() => openSection("plan")}
            >
              <CalendarDays size={18} aria-hidden="true" />
              Üretim planı
            </button>
          ) : null}
          <button
            type="button"
            disabled={navigationBlocked}
            aria-current={section === "completed" ? "page" : undefined}
            onClick={() => openSection("completed")}
          >
            <CircleCheck size={18} aria-hidden="true" />
            Tamamlananlar
          </button>
          {navigationGuard ? (
            <p id="sales-navigation-guard" role="status">
              {navigationGuard}
            </p>
          ) : null}
        </nav>
        <div
          className={
            "anl-sales-home-main" +
            (work.selectedId ? " anl-workspace-document" : "")
          }
        >
          {work.selectedId ? (
            <>
              {work.detailState === "loading" ? (
                <SystemState
                  title="Talep yükleniyor"
                  description="Güncel bilgiler okunuyor."
                  tone="loading"
                />
              ) : null}
              {work.detailState === "error" ||
              work.detailState === "unauthorized" ? (
                <SystemState
                  title="Talep açılamadı"
                  description={work.detailFault?.message ?? "Kaydı yenileyin."}
                  tone="danger"
                  primaryAction={{
                    label: "Yeniden dene",
                    onClick: work.refreshDetail,
                  }}
                />
              ) : null}
              {work.detailState === "ready" && detail ? (
                <section
                  className="anl-sales-task-detail"
                  aria-label="Satış talebi"
                >
                  <LabelRecordHeader detail={detail} editorial />
                  {work.feedback ? (
                    <div
                      className={"anl-feedback is-" + work.feedback.tone}
                      role="status"
                    >
                      {work.feedback.message}
                    </div>
                  ) : null}
                  {work.mutationFault ? (
                    <SystemState
                      title={
                        work.stale ? "Kayıt değişti" : "İşlem tamamlanmadı"
                      }
                      description={work.mutationFault.message}
                      tone="danger"
                      mode="banner"
                      primaryAction={
                        work.stale
                          ? {
                              label: "Güncel kaydı al",
                              onClick: work.refreshDetail,
                            }
                          : work.canRetryMutation
                            ? {
                                label: "Aynı işlemi yeniden dene",
                                onClick: work.retryMutation,
                              }
                            : undefined
                      }
                    />
                  ) : null}
                  {detail.state === "sales_pending" ||
                  detail.state === "returned_to_sales" ? (
                    <div className="anl-sales-task-workspace">
                      <SalesPlanBrief key={detail.id} detail={detail} />
                      <SalesForm
                        detail={detail}
                        draft={work.draft}
                        policy={session.policy}
                        demo={session.demo}
                        busy={work.mutationBusy}
                        dirty={requirementsDirty}
                        pendingUpload={work.pendingUpload}
                        disabled={work.stale}
                        fault={work.mutationFault}
                        onChange={work.changeRequirement}
                        onSave={work.saveDraft}
                        onSubmit={work.confirmSales}
                        onOpen={work.openFile}
                        downloaded={work.downloaded}
                        fileLoading={work.fileLoading}
                        referenceUpload={
                          <section
                            key={detail.id + ":" + detail.files.length}
                            className="anl-sales-reference-upload"
                            aria-label="Kaynak dosya ekleme"
                          >
                            <FileUpload
                              draft={work.upload}
                              policy={session.policy}
                              busy={work.mutationBusy}
                              disabled={work.stale || requirementsDirty}
                              referenceOnly
                              compact
                              onChange={work.setUpload}
                              onUpload={work.uploadFile}
                            />
                          </section>
                        }
                      />
                    </div>
                  ) : (
                    <SystemState
                      title="Satış adımı tamamlandı"
                      description={`Talep şu an ${stateLabels[detail.state].toLocaleLowerCase("tr-TR")} aşamasında. Bu ekranda satış değişikliği yapılamaz.`}
                    />
                  )}
                </section>
              ) : null}
            </>
          ) : section === "mine" ? (
            <section aria-label="Bana atanan etiket işleri">
              <header className="anl-sales-page-head">
                <div>
                  <h1>Etiket işleri</h1>
                  <p>
                    Talimatı tamamlayın, pazarlamaya iletin ve süreci takip
                    edin.
                  </p>
                </div>
                <button
                  type="button"
                  className="anl-button is-text"
                  disabled={work.listState === "loading"}
                  onClick={work.retryList}
                >
                  <RefreshCw size={14} aria-hidden="true" />
                  Yenile
                </button>
              </header>
              <div className="anl-sales-toolbar">
                <label className="anl-search-control">
                  <Search size={17} aria-hidden="true" />
                  <span className="anl-visually-hidden">İşlerde ara</span>
                  <input
                    type="search"
                    value={work.query.q}
                    placeholder="İş emri, müşteri veya ürün ara"
                    onChange={(event) =>
                      work.setQuery({
                        ...work.query,
                        q: event.target.value,
                        page: 1,
                      })
                    }
                  />
                </label>
                <div className="anl-sales-state-filter" aria-label="İş durumu">
                  <SlidersHorizontal size={16} aria-hidden="true" />
                  <button
                    type="button"
                    aria-pressed={!work.query.state}
                    onClick={() =>
                      work.setQuery({
                        ...work.query,
                        state: undefined,
                        page: 1,
                      })
                    }
                  >
                    Tümü
                  </button>
                  <button
                    type="button"
                    aria-pressed={work.query.state === "sales_pending"}
                    onClick={() =>
                      work.setQuery({
                        ...work.query,
                        state: "sales_pending",
                        page: 1,
                      })
                    }
                  >
                    Bilgi bekleyen
                  </button>
                  <button
                    type="button"
                    aria-pressed={work.query.state === "returned_to_sales"}
                    onClick={() =>
                      work.setQuery({
                        ...work.query,
                        state: "returned_to_sales",
                        page: 1,
                      })
                    }
                  >
                    Düzeltme
                  </button>
                </div>
              </div>
              {work.listState === "loading" ? (
                <SystemState
                  title="İşler yükleniyor"
                  description="Size atanmış talepler okunuyor."
                  tone="loading"
                />
              ) : null}
              {work.listState === "error" ||
              work.listState === "unauthorized" ? (
                <SystemState
                  title="İşler alınamadı"
                  description={
                    work.listFault?.message ?? "Oturumu kontrol edin."
                  }
                  tone="danger"
                  primaryAction={{
                    label: "Yeniden dene",
                    onClick: work.retryList,
                  }}
                />
              ) : null}
              {work.listState === "ready" && needsAction.length === 0 ? (
                <SystemState
                  title={
                    work.query.q || work.query.state
                      ? "Aramaya uygun iş yok"
                      : "Şu an bekleyen iş yok"
                  }
                  description={
                    work.query.q || work.query.state
                      ? "Aramayı veya durum filtresini değiştirin."
                      : "Koordinasyon size bir etiket talebi atadığında burada görünecek."
                  }
                />
              ) : null}
              {work.listState === "ready" && needsAction.length > 0 ? (
                <div
                  className="anl-sales-task-table-wrap"
                  role="region"
                  aria-label="Satış iş tablosu"
                  tabIndex={0}
                >
                  <table className="anl-sales-task-table">
                    <caption className="anl-visually-hidden">
                      Satış işlemi bekleyen etiket talepleri
                    </caption>
                    <thead>
                      <tr>
                        <th>Hat / iş emri</th>
                        <th>Müşteri / ürün</th>
                        <th>Plan açıklaması</th>
                        <th>Durum</th>
                        <th aria-label="İşlem" />
                      </tr>
                    </thead>
                    <tbody>
                      {needsAction.map((item) => (
                        <tr key={item.id}>
                          <td data-label="Hat / iş emri">
                            <strong>
                              {item.lineName ?? "Hat belirtilmemiş"}
                            </strong>
                            <span>
                              {item.workOrder ?? "İş emri bekleniyor"}
                            </span>
                            <small>{item.requestNo}</small>
                          </td>
                          <td data-label="Müşteri / ürün">
                            <strong>
                              {item.customerName ?? "Müşteri belirtilmemiş"}
                            </strong>
                            <span>
                              {item.productName ?? "Ürün belirtilmemiş"}
                            </span>
                          </td>
                          <td data-label="Plan açıklaması">
                            <button
                              type="button"
                              className="anl-sales-explanation-toggle"
                              aria-expanded={expandedExplanation === item.id}
                              onClick={() =>
                                setExpandedExplanation(
                                  expandedExplanation === item.id
                                    ? null
                                    : item.id,
                                )
                              }
                            >
                              <span
                                className={
                                  expandedExplanation === item.id
                                    ? "anl-sales-explanation"
                                    : "anl-sales-explanation-preview"
                                }
                              >
                                {item.planExplanation || "Açıklama yok"}
                              </span>
                              <small>
                                {expandedExplanation === item.id
                                  ? "Daralt"
                                  : "Tamamını göster"}
                              </small>
                            </button>
                          </td>
                          <td data-label="Durum">
                            <span
                              className={
                                item.state === "returned_to_sales"
                                  ? "anl-sales-task-state is-returned"
                                  : "anl-sales-task-state"
                              }
                            >
                              {stateLabels[item.state]}
                            </span>
                            {item.qualityWarning ? (
                              <small>{item.qualityWarning}</small>
                            ) : null}
                          </td>
                          <td data-label="İşlem">
                            <button
                              type="button"
                              className="anl-sales-primary"
                              onClick={() => work.select(item.id)}
                            >
                              Talimatı aç
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {work.listState === "ready" &&
              work.list &&
              work.list.total > work.list.pageSize ? (
                <div className="anl-sales-list-pages">
                  <span>
                    Sayfa {work.list.page} /{" "}
                    {Math.ceil(work.list.total / work.list.pageSize)}
                  </span>
                  <button
                    type="button"
                    disabled={work.list.page <= 1}
                    onClick={() =>
                      work.setQuery({
                        ...work.query,
                        page: (work.list?.page ?? 1) - 1,
                      })
                    }
                  >
                    Önceki
                  </button>
                  <button
                    type="button"
                    disabled={
                      work.list.page * work.list.pageSize >= work.list.total
                    }
                    onClick={() =>
                      work.setQuery({
                        ...work.query,
                        page: (work.list?.page ?? 1) + 1,
                      })
                    }
                  >
                    Sonraki
                  </button>
                </div>
              ) : null}
            </section>
          ) : section === "completed" ? (
            <CompletedSalesList
              items={completedItems}
              list={work.list}
              loadState={work.listState}
              fault={work.listFault}
              onSelect={work.select}
              onRetry={work.retryList}
              onPage={(page) => work.setQuery({ ...work.query, page })}
            />
          ) : (
            <SalesPlanSheet workspace={plan} />
          )}
        </div>
      </div>
      <ConfirmDialog
        open={work.confirmation !== null}
        title={
          work.confirmation === "discard"
            ? "Kaydedilmemiş değişiklikleri bırak?"
            : "Satış bilgilerini pazarlamaya gönder?"
        }
        confirmLabel={
          work.confirmation === "discard"
            ? "Değişiklikleri bırak"
            : "Teyit et ve gönder"
        }
        busy={work.mutationBusy}
        onCancel={work.cancelConfirmation}
        onConfirm={work.confirm}
      >
        <p>
          {work.confirmation === "discard"
            ? "Kaydedilmemiş form veya dosya seçimi bırakılacak. Sunucudaki kayıt korunur."
            : `${detail?.requestNo ?? "Talep"} için kaydedilmiş satış bilgileri yeni bir gereklilik revizyonu olarak pazarlamaya gönderilecek.`}
        </p>
      </ConfirmDialog>
    </div>
  );
}

function SalesPlanBrief({ detail }: { detail: RequestDetail }) {
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const text = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const element = text.current;
    if (!element || expanded) return;
    const measure = () =>
      setTruncated(element.scrollHeight > element.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [expanded, truncated]);
  const expandable = truncated || expanded;
  const Content = expandable ? "button" : "div";
  return (
    <aside className="anl-sales-plan-brief" aria-label={detail.planRow ? "İşin plan bilgileri" : "İş emri kaynağı"}>
      <div className="anl-plan-brief-heading">
        <h2>{detail.planRow ? "Plan açıklaması" : "Kaynak açıklaması"}</h2>
      </div>
      <Content
        className="anl-plan-brief-toggle"
        type={expandable ? "button" : undefined}
        aria-expanded={expandable ? expanded : undefined}
        onClick={expandable ? () => setExpanded((value) => !value) : undefined}
      >
        <span ref={text} className={expanded ? "is-expanded" : undefined}>
          {(detail.planRow ? planExplanation(detail.planRow) : detail.planExplanation) ||
            "Bu talep için kaynak açıklaması bulunmuyor."}
        </span>
        {expandable ? (
          <small>{expanded ? "Daralt" : "Tamamını göster"}</small>
        ) : null}
      </Content>
    </aside>
  );
}
